import { useCallback, useRef, useState } from 'react';

import type React from 'react';

/**
 * Where a drop would land: a position in the row order, *and* the chapter that
 * position belongs to (PLAT-11075 DS1). One gap can be the end of one chapter
 * or the start of the next, so the index alone does not say which.
 */
export type ReelDropTarget = {
  /** Insertion point in the row order, in 0..rowCount. */
  index: number;
  /** The row that draws the insertion line, and which edge of it. */
  rowId?: string;
  edge?: 'above' | 'below';
  /** The chapter the Section lands in; absent on a reel with no chapters. */
  chapterId?: string;
  /** The Section becomes that chapter's first — the arrival that moves a boundary. */
  opensChapter?: boolean;
};

/**
 * PLAT-11056 — where a dragged *Chapter* would land: a boundary in chapter
 * order, never a position inside one (DS2).
 */
export type ReelChapterDropTarget = {
  /**
   * The chapter it lands above, in 0..chapterCount — the end of the reel.
   *
   * A boundary and nothing else. Deliberately *not* the id of the chapter whose
   * zone the pointer is in: the rule that closes a chapter stands for the
   * boundary above the *next* one, so an id would name the wrong heading for
   * the insertion line half the time.
   */
  index: number;
};

export type ReelEditorDrag = {
  /** Index of the row being dragged, or null when no Section drag is in flight. */
  draggingIndex: number | null;
  /** The chapter being dragged; only one kind of drag runs at a time (DS2). */
  draggingChapterId: string | null;
  /** Where a drop would land, or null when the pointer is outside the list. */
  dropTarget: ReelDropTarget | null;
  chapterDropTarget: ReelChapterDropTarget | null;
  handleDragStart: (e: React.DragEvent, index: number) => void;
  handleChapterDragStart: (
    e: React.DragEvent,
    chapterId: string,
    chapterIndex: number,
  ) => void;
  /** A row: the nearer edge wins, and its top edge may open its chapter. */
  handleRowDragOver: (
    e: React.DragEvent,
    index: number,
    rowId: string,
    chapterId?: string,
    isFirstInChapter?: boolean,
    chapterIndex?: number,
  ) => void;
  /** A divider, a chapter heading or an empty chapter: the edge is given. */
  handleGapDragOver: (
    e: React.DragEvent,
    index: number,
    chapterId?: string,
    opensChapter?: boolean,
    rowId?: string,
    edge?: 'above' | 'below',
    chapterIndex?: number,
  ) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragEnd: () => void;
};

/** What is in flight, and where it started. */
type DragSource =
  | { kind: 'section'; index: number }
  | { kind: 'chapter'; chapterId: string; index: number };

/** Cheap identity for a target, so a pointer move only re-renders on a change. */
const targetKey = (target: ReelDropTarget) =>
  `${target.index}:${target.rowId ?? ''}:${target.edge ?? ''}:${target.chapterId ?? ''}:${target.opensChapter ? 1 : 0}`;

/**
 * PLAT-11053 — native HTML5 drag reordering for the Reel Editor's rows, in the
 * same shape as `useReelBeatReorder` (no library).
 *
 * Drop targets are the gaps *between* rows, not the rows themselves (DS4): a
 * drop on a row is ambiguous about before-or-after, whereas an insertion point
 * makes the outcome legible before release.
 *
 * PLAT-11075 — a gap is now a chapter as well as a position, and every zone in
 * the list reports one: rows pick the nearer edge, while dividers, headings and
 * empty chapters are handed theirs (DS5). The hook owns only the gesture;
 * *which* target a zone stands for is the panel's to decide, since the panel is
 * what knows where the headings are. Moving anything is likewise the caller's
 * job, via `editor.commandMoveReelSection` and the reel write beside it.
 *
 * PLAT-11056 — a Chapter can be dragged too, and it lands *between* chapters
 * rather than inside one (DS2). So every zone hands in both readings — its
 * Section target as before, plus the index of the chapter it belongs to — and
 * the hook keeps the one that matches what is being dragged. That is what stops
 * a zone from going dead when the kind of drag changes: the list stays
 * droppable everywhere either way (PLAT-11075 DS5).
 */
export function useReelEditorDrag(
  onMove: (fromIndex: number, target: ReelDropTarget) => void,
  onMoveChapter?: (fromChapterIndex: number, toChapterIndex: number) => void,
): ReelEditorDrag {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [draggingChapterId, setDraggingChapterId] = useState<string | null>(
    null,
  );
  const [dropTarget, setDropTarget] = useState<ReelDropTarget | null>(null);
  const [chapterDropTarget, setChapterDropTarget] =
    useState<ReelChapterDropTarget | null>(null);
  const dragSourceRef = useRef<DragSource | null>(null);
  // `dragover` fires continuously while the pointer moves; the gap under it
  // changes only when it crosses a row's midpoint or another zone. Tracking the
  // last one keeps the panel off React's render path for the events in between.
  const lastTargetKeyRef = useRef<string | null>(null);
  // Read on drop: the targets are state, and the drop handler is a callback the
  // rows captured on an earlier render.
  const dropTargetRef = useRef<ReelDropTarget | null>(null);
  const chapterTargetRef = useRef<ReelChapterDropTarget | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragSourceRef.current = { kind: 'section', index };
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag without payload on the transfer.
    e.dataTransfer.setData('text/plain', `reel-section:${index}`);
  }, []);

  const handleChapterDragStart = useCallback(
    (e: React.DragEvent, chapterId: string, chapterIndex: number) => {
      dragSourceRef.current = {
        kind: 'chapter',
        chapterId,
        index: chapterIndex,
      };
      setDraggingChapterId(chapterId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `reel-chapter:${chapterId}`);
    },
    [],
  );

  // `preventDefault` is what makes a zone droppable at all, so every zone that
  // reports a target has to run this — a gap that does not is a dead strip the
  // drag cancels on (DS5).
  const setTarget = useCallback(
    (e: React.DragEvent, target: ReelDropTarget, chapterIndex?: number) => {
      const source = dragSourceRef.current;
      if (!source) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';

      // A Chapter drag reads the list as a list of chapters: the zone's
      // Section target is not what is being aimed at, only the chapter it
      // belongs to (DS2).
      if (source.kind === 'chapter') {
        if (chapterIndex === undefined) return;
        const key = `chapter:${chapterIndex}`;
        if (key === lastTargetKeyRef.current) return;
        lastTargetKeyRef.current = key;
        const chapterTarget = { index: chapterIndex };
        chapterTargetRef.current = chapterTarget;
        setChapterDropTarget(chapterTarget);
        return;
      }

      const key = targetKey(target);
      if (key === lastTargetKeyRef.current) return;
      lastTargetKeyRef.current = key;
      dropTargetRef.current = target;
      setDropTarget(target);
    },
    [],
  );

  const handleRowDragOver = useCallback(
    (
      e: React.DragEvent,
      index: number,
      rowId: string,
      chapterId?: string,
      isFirstInChapter?: boolean,
      chapterIndex?: number,
    ) => {
      if (dragSourceRef.current === null) return;

      // Above the row's midpoint inserts before it, below inserts after.
      const rect = e.currentTarget.getBoundingClientRect();
      const isPastMidpoint = e.clientY > rect.top + rect.height / 2;

      setTarget(
        e,
        isPastMidpoint
          ? { index: index + 1, rowId, edge: 'below', chapterId }
          : {
              index,
              rowId,
              edge: 'above',
              chapterId,
              // The gap above a chapter's first row sits under its heading, so
              // it is that chapter's start rather than the end of the one above
              // — whose own gap is the rule above the heading (DS1).
              opensChapter: isFirstInChapter,
            },
        chapterIndex,
      );
    },
    [setTarget],
  );

  const handleGapDragOver = useCallback(
    (
      e: React.DragEvent,
      index: number,
      chapterId?: string,
      opensChapter?: boolean,
      rowId?: string,
      edge?: 'above' | 'below',
      chapterIndex?: number,
    ) => {
      setTarget(
        e,
        { index, rowId, edge, chapterId, opensChapter },
        chapterIndex,
      );
    },
    [setTarget],
  );

  const handleDragLeave = useCallback(() => {
    lastTargetKeyRef.current = null;
    dropTargetRef.current = null;
    chapterTargetRef.current = null;
    setDropTarget(null);
    setChapterDropTarget(null);
  }, []);

  const reset = useCallback(() => {
    dragSourceRef.current = null;
    lastTargetKeyRef.current = null;
    dropTargetRef.current = null;
    chapterTargetRef.current = null;
    setDraggingIndex(null);
    setDraggingChapterId(null);
    setDropTarget(null);
    setChapterDropTarget(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const source = dragSourceRef.current;
      const target = dropTargetRef.current;
      const chapterTarget = chapterTargetRef.current;
      reset();

      if (!source) return;

      if (source.kind === 'chapter') {
        if (chapterTarget) onMoveChapter?.(source.index, chapterTarget.index);
        return;
      }

      // A drop that changes no index can still change a chapter (DS7), so the
      // caller decides what is worth doing — not this hook.
      if (target === null) return;
      onMove(source.index, target);
    },
    [onMove, onMoveChapter, reset],
  );

  return {
    draggingIndex,
    draggingChapterId,
    dropTarget,
    chapterDropTarget,
    handleDragStart,
    handleChapterDragStart,
    handleRowDragOver,
    handleGapDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd: reset,
  };
}
